import { consentStorage } from './storage'

const completedWhatsappContactAttempts = new Set<string>()
let adPlatformsConfigured = false

function hasAdConsent() {
  return consentStorage.getConsent() === 'granted'
}

function getStringProp(props: Record<string, unknown> | undefined, key: string) {
  const value = props?.[key]
  return typeof value === 'string' ? value : undefined
}

function getContactAttemptKey(props: Record<string, unknown> | undefined) {
  return getStringProp(props, 'contact_attempt_id')
}

function trackAdPlatformLead(props: Record<string, unknown> | undefined) {
  if (!hasAdConsent()) return

  const attemptKey = getContactAttemptKey(props)
  if (attemptKey) {
    if (completedWhatsappContactAttempts.has(attemptKey)) return
    completedWhatsappContactAttempts.add(attemptKey)
  }

  const conversionLabel = import.meta.env.VITE_GOOGLE_ADS_CONVERSION_LABEL
  const googleAdsId = import.meta.env.VITE_GOOGLE_ADS_ID
  if (googleAdsId && conversionLabel && typeof window.gtag === 'function') {
    window.gtag('event', 'conversion', {
      send_to: `${googleAdsId}/${conversionLabel}`,
    })
  }

  if (import.meta.env.VITE_META_PIXEL_ID && typeof window.fbq === 'function') {
    window.fbq('track', 'Lead', {
      content_name: EVENTS.WHATSAPP_CONTACT,
      professional_id: getStringProp(props, 'professional_id'),
      professional_name: getStringProp(props, 'professional_name'),
      profession_name: getStringProp(props, 'profession_name'),
      contact_attempt_id: attemptKey,
    })
  }
}

function maybeMirrorEventToAdPlatforms(event: string, props?: Record<string, unknown>) {
  if (event !== EVENTS.WHATSAPP_CONTACT) return
  if (props?.stage !== WHATSAPP_STAGE.OPENED) return

  trackAdPlatformLead(props)
}

function sanitizePagePath(path: string) {
  return new URL(path || '/', window.location.origin).pathname
}

function buildPageLocation(path: string) {
  return `${window.location.origin}${path}`
}

function configureAdPlatforms() {
  if (!hasAdConsent() || adPlatformsConfigured) return

  if (typeof window.gtag === 'function') {
    window.gtag('consent', 'update', {
      ad_storage: 'granted',
      analytics_storage: 'granted',
    })
    if (import.meta.env.VITE_GOOGLE_ADS_ID) {
      window.gtag('config', import.meta.env.VITE_GOOGLE_ADS_ID)
    }
    if (import.meta.env.VITE_GA4_MEASUREMENT_ID) {
      window.gtag('config', import.meta.env.VITE_GA4_MEASUREMENT_ID, {
        send_page_view: false,
      })
    }
  }

  if (import.meta.env.VITE_META_PIXEL_ID && typeof window.fbq === 'function') {
    window.fbq('consent', 'grant')
    window.fbq('init', import.meta.env.VITE_META_PIXEL_ID)
  }

  adPlatformsConfigured = true
}

function revokeAdPlatformConsent() {
  if (typeof window.gtag === 'function') {
    window.gtag('consent', 'update', {
      ad_storage: 'denied',
      analytics_storage: 'denied',
    })
  }
  if (typeof window.fbq === 'function') {
    window.fbq('consent', 'revoke')
  }
}

// ============================================================================
// Core wrapper
// ============================================================================

export const analytics = {
  capture(event: string, props?: Record<string, unknown>) {
    window.posthog?.capture(event, props)
    maybeMirrorEventToAdPlatforms(event, props)
  },
  identify(distinctId: string, props?: Record<string, unknown>) {
    window.posthog?.identify(distinctId, props)
  },
  reset() {
    window.posthog?.reset()
  },
  trackPageView(path: string) {
    if (!hasAdConsent()) return

    configureAdPlatforms()
    const pagePath = sanitizePagePath(path)

    if (import.meta.env.VITE_GA4_MEASUREMENT_ID && typeof window.gtag === 'function') {
      window.gtag('event', 'page_view', {
        send_to: import.meta.env.VITE_GA4_MEASUREMENT_ID,
        page_path: pagePath,
        page_location: buildPageLocation(pagePath),
        page_title: document.title,
      })
    }

    if (import.meta.env.VITE_META_PIXEL_ID && typeof window.fbq === 'function') {
      window.fbq('track', 'PageView')
    }
  },
  // Consent — PostHog is initialized with opt_out_capturing_by_default in index.html,
  // so nothing is captured until optIn() is called from the consent banner.
  optIn() {
    window.posthog?.opt_in_capturing?.()
    configureAdPlatforms()
  },
  optOut() {
    revokeAdPlatformConsent()
    window.posthog?.opt_out_capturing?.()
  },
  recordConsentDecision(decision: 'accepted' | 'declined' | 'dismissed') {
    if (decision === 'accepted') {
      window.posthog?.capture(EVENTS.COOKIE_CONSENT_DECISION, { decision })
      return
    }

    window.posthog?.opt_in_capturing?.()
    window.posthog?.capture(EVENTS.COOKIE_CONSENT_DECISION, { decision })
    window.posthog?.opt_out_capturing?.()
  },
}

export function createContactAttemptId(professionalId: string) {
  if (globalThis.crypto?.randomUUID) return globalThis.crypto.randomUUID()
  return `${professionalId}:${Date.now()}:${Math.random().toString(36).slice(2)}`
}

// ============================================================================
// Event name constants — use these everywhere; string literals silently create
// phantom events in PostHog on typo.
// ============================================================================

export const EVENTS = {
  ACCOUNT_CREATED: 'account_created',
  COOKIE_CONSENT_DECISION: 'cookie_consent_decision',
  WHATSAPP_CONTACT: 'whatsapp_contact',
} as const

export const WHATSAPP_STAGE = {
  UNAUTHENTICATED: 'unauthenticated',
  UNVALIDATED: 'unvalidated',
  OPENED: 'opened',
  FAILED: 'failed',
} as const

export const WHATSAPP_VIA = {
  DIRECT: 'direct',
  PENDING_ACTION: 'pending_action',
} as const

// ============================================================================
// Prop builder — shared shape for whatsapp_contact across both call sites
// ============================================================================

export function buildWhatsappContactProps(
  base: {
    professional_id: string
    professional_name?: string
    profession_name?: string
    contact_attempt_id?: string
  },
  via: (typeof WHATSAPP_VIA)[keyof typeof WHATSAPP_VIA]
) {
  return { ...base, via }
}

// ============================================================================
// Event catalogue
// ============================================================================
//
// whatsapp_contact
//   Fires on every WhatsApp contact interaction. A single logical contact attempt by an
//   unauthenticated/unvalidated user emits TWO events: a redirect-intent stage at click
//   time, then 'opened'/'failed' after login resumes the pending action. Always filter by
//   `stage` — never use raw count.
//
//   stage:
//     'unauthenticated' — clicked but not logged in; redirect to login
//     'unvalidated'     — logged in but account not validated; redirect to profile
//     'opened'          — reached the window.open call (a contact attempt). This is a
//                         BROAD signal: we deliberately do NOT distinguish popup-blocked
//                         from truly-opened. Cross-browser block detection is unreliable
//                         (Safari/Firefox may block yet return non-null), and the product
//                         only needs a contact-attempt count, not open-vs-blocked fidelity.
//     'failed'          — API error, no contact number on file, or corrupted pending
//                         action with no professional_id (reason prop set)
//
//   via:
//     'direct'          — triggered via the WhatsApp button directly (any auth state)
//     'pending_action'  — resumed from a stored pending action after login/validation redirect
//
//   Canonical funnel (single source of truth — every consumer must filter by stage):
//     attempts        = count(stage in ['opened','failed'])           // actual contacts tried
//     redirect-intent = count(stage in ['unauthenticated','unvalidated'])  // gated, will resume post-auth
//     success-rate    = count(stage = 'opened') / attempts
//   Do not sum all stages as "total clicks": a gated click is later counted again as
//   opened/failed once the pending action resumes, so it would double-count.
//
// account_created
//   SEMANTIC (decided): raw backend account-creation count, NOT onboarding success.
//   Fires right after registerMutation resolves — before the profile refetch and before
//   identify(). It therefore counts every account the backend created, including ones
//   where the subsequent refetch blips and the session never establishes. This is
//   intentional: we want creation volume, not session-established volume.
//
//   Anonymous-person edge: identify() runs only after a successful refetch. With
//   person_profiles:'identified_only', if refetch fails the event lands on an anonymous
//   person and won't merge into a user profile. Accepted under the raw-creation semantic.
//
//   identifier_type: 'phone' | 'email'
